import { useEffect, useRef, useState } from "react";
import { penalty as hapticPenalty, reward as hapticReward, timerExpire, timerTick } from "../lib/haptic";
import { useLanguage } from "../state/language";
import { isDebugEnabled } from "../lib/debug";
import { Confetti } from "./Confetti";

interface Props {
  kind: "reward" | "penalty";
  text: string;
  playerName: string;
  onDone: () => void;
  helperNames?: string[];
  timerDurationSec?: number | null;
  cardId?: string;
}

const KIND_THEME = {
  reward: {
    glyph: "🏆",
    title_uk: "НАГОРОДА",
    color: "#fbbf24",
    glow: "rgba(251, 191, 36, 0.7)",
    bg: "linear-gradient(160deg, rgba(251,191,36,0.18), rgba(34,197,94,0.12))",
    accents: ["✨", "🎉", "💎", "⭐"],
  },
  penalty: {
    glyph: "💀",
    title_uk: "ПОКАРАННЯ",
    color: "#fb7185",
    glow: "rgba(251, 113, 133, 0.7)",
    bg: "linear-gradient(160deg, rgba(251,113,133,0.18), rgba(91,33,182,0.18))",
    accents: ["🔥", "⚡", "💔", "🥶"],
  },
} as const;

export function ConsequenceStage({ kind, text, playerName, helperNames, onDone, timerDurationSec, cardId }: Props) {
  const { t } = useLanguage();
  const debug = isDebugEnabled();
  const [remaining, setRemaining] = useState(timerDurationSec ?? 0);
  const doneRef = useRef(false);
  const timed = timerDurationSec != null && timerDurationSec > 0;

  function finish() {
    if (doneRef.current) return;
    doneRef.current = true;
    onDone();
  }

  useEffect(() => {
    doneRef.current = false;
    setRemaining(timerDurationSec ?? 0);
    if (kind === "reward") hapticReward();
    else hapticPenalty();
    if (!timed) return;
    const total = (timerDurationSec ?? 0) * 1000;
    const start = performance.now();
    let raf = 0;
    let lastTickedSecond = -1;
    const step = (now: number) => {
      const left = Math.max(0, (total - (now - start)) / 1000);
      setRemaining(left);
      const ceilLeft = Math.ceil(left);
      if (left > 0 && ceilLeft <= 3 && ceilLeft !== lastTickedSecond) {
        lastTickedSecond = ceilLeft;
        timerTick();
      }
      if (left <= 0) {
        timerExpire();
        finish();
        return;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, timerDurationSec]);

  const theme = KIND_THEME[kind];
  const n = helperNames?.length ?? 0;
  const chipText = n === 1 ? "text-xl" : n <= 3 ? "text-base" : n <= 5 ? "text-sm" : "text-xs";
  const chipPad = n === 1 ? "px-4 py-1.5" : n <= 3 ? "px-3 py-1" : "px-2.5 py-0.5";
  const avSize = n === 1 ? "w-7 h-7 text-sm" : n <= 3 ? "w-5 h-5 text-[11px]" : "w-4 h-4 text-[10px]";
  const helperLabel = n === 1
    ? (kind === "reward" ? "Виконує для нього" : "Виконує над ним")
    : (kind === "reward" ? "Виконують для нього" : "Виконують над ним");

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col items-center justify-center px-6 backdrop-blur-md overflow-hidden"
      style={{ background: theme.bg, animation: "pp-phase-fade 1500ms ease-out" }}
    >
      {/* Reward = celebration; penalty keeps its ominous float instead. */}
      {kind === "reward" && <Confetti count={30} />}
      {theme.accents.map((g, i) => (
        <span
          key={i}
          className="absolute text-5xl pointer-events-none select-none"
          style={{
            top: `${15 + (i * 17) % 70}%`,
            left: `${8 + (i * 23) % 84}%`,
            animation: `pp-react-float ${3 + (i % 3)}s ease-in-out ${i * 200}ms infinite`,
            opacity: 0.75,
            filter: `drop-shadow(0 0 12px ${theme.glow})`,
          }}
        >
          {g}
        </span>
      ))}
      <div
        className="absolute"
        style={{
          width: 320,
          height: 320,
          borderRadius: "50%",
          border: `2px solid ${theme.color}`,
          opacity: 0.4,
          animation: "pp-phase-ring 1300ms ease-out",
        }}
      />
      <div
        className="relative text-7xl mb-2 animate-phase-pop"
        style={{ filter: `drop-shadow(0 0 24px ${theme.glow})` }}
      >
        {theme.glyph}
      </div>
      <div
        className="relative font-display text-3xl font-black uppercase tracking-wide mb-2"
        style={{ color: theme.color, textShadow: `0 0 18px ${theme.glow}` }}
      >
        {theme.title_uk}
      </div>
      <div
        className="relative font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none mb-1"
        style={{ color: "#fff", textShadow: `0 0 18px ${theme.glow}, 0 0 36px rgba(0,0,0,0.45)` }}
      >
        {playerName}
      </div>
      {n > 0 && (
        <div className="relative flex flex-col items-center gap-2 mb-4 mt-1">
          <span className="text-xs uppercase tracking-[0.2em] opacity-85 font-bold">
            {helperLabel}
          </span>
          <div className="flex flex-wrap items-center justify-center gap-2 max-w-[340px]">
            {helperNames!.map((name, i) => (
              <span
                key={i}
                className={`inline-flex items-center gap-2 rounded-full bg-white/20 border border-white/35 ${chipPad} ${chipText} font-bold`}
                style={{ boxShadow: `0 0 12px ${theme.glow}` }}
              >
                <span className={`${avSize} rounded-full bg-white/30 flex items-center justify-center font-black`}>
                  {name[0]?.toUpperCase()}
                </span>
                {name}
              </span>
            ))}
          </div>
        </div>
      )}
      <div
        className="relative font-display font-bold text-xl text-center max-w-md text-balance leading-snug px-4 mb-5"
        style={{ color: "#fff", textShadow: `0 0 14px ${theme.glow}` }}
      >
        {text}
      </div>
      {debug && cardId && (
        <div className="relative -mt-3 mb-4 text-[10px] font-mono opacity-60 px-2 py-0.5 rounded bg-black/30 border border-white/10 select-text">
          🛠 {cardId}
        </div>
      )}
      {timed ? (
        <div
          className={`relative w-32 h-32 select-none ${remaining <= 5 ? "pp-timer-shake" : ""}`}
          style={{ filter: `drop-shadow(0 0 24px ${theme.glow})` }}
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: `2px solid ${theme.color}`,
              opacity: 0.3,
              animation: `pp-timer-pulse ${remaining <= 5 ? "0.7s" : "1.4s"} ease-out infinite`,
            }}
          />
          <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
            <circle cx="50" cy="50" r="44" stroke="white" strokeOpacity="0.15" strokeWidth="4" fill="none" />
            <circle
              cx="50"
              cy="50"
              r="44"
              stroke={theme.color}
              strokeWidth="6"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 44}`}
              strokeDashoffset={`${2 * Math.PI * 44 * (1 - remaining / (timerDurationSec ?? 1))}`}
              style={{
                transition: "stroke-dashoffset 100ms linear",
                filter: `drop-shadow(0 0 6px ${theme.glow})`,
              }}
            />
          </svg>
          <div
            className="absolute inset-0 flex items-center justify-center font-display font-black tabular-nums"
            style={{ color: "#fff", fontSize: 48, textShadow: `0 0 12px ${theme.glow}` }}
          >
            {Math.ceil(remaining)}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={finish}
          className="relative px-10 py-4 rounded-full font-display font-black text-2xl uppercase tracking-wide bg-white text-black active:scale-95 transition pp-pulse-cta"
          style={{ boxShadow: `0 0 28px ${theme.glow}, 0 0 56px ${theme.glow}` }}
        >
          {t("consequence_continue")} →
        </button>
      )}
    </div>
  );
}
